import { Fragment, useCallback, useEffect, useState } from 'react';
import { useScreenSize } from '../../../../hooks/useScreenSize';
import { useWindowBreakpoints } from '../../../../hooks/useWindowBreakpoints';
import { IExercise, IExerciseAlternative } from '../../../../models/exercise';
import { Button } from '../../../atoms/Button';
import { ContentActions } from '../../../organism/ContentActions';
import { Modal } from '../../../templates/Modal';
import InnerHTMLContainer from '../../../molecules/InnerHTMLContainer';
import { AlternativesList } from '../../AlternativesList';

interface IQuizExerciseProps {
  exercise: IExercise;
  totalExercises: number;
  currentExerciseIndex: number;
  handleNextExercise: () => void;
  handlePreviousExercise: () => void;
  onShowCorrectAnswer: (selectedAlternative: IExerciseAlternative) => void;
  isOpen: boolean;
  onClose: () => void;
}

export function QuizExercise({
  exercise,
  totalExercises,
  currentExerciseIndex,
  handleNextExercise,
  handlePreviousExercise,
  onShowCorrectAnswer,
  isOpen,
  onClose,
}: IQuizExerciseProps) {
  const [selectedAlternative, setSelectedAlternative] =
    useState<IExerciseAlternative | null>(null);

  const [showResult, setShowResult] = useState(false);
  const [showCorrectAnswer, setShowCorrectAnswer] = useState(false);

  const isCorrect = useCallback(() => {
    if (!exercise) return false;

    const correctAlternative = exercise.alternatives.find(
      (alternative) => alternative.correct
    );

    if (!correctAlternative || !selectedAlternative) return false;

    return selectedAlternative.id === correctAlternative.id;
  }, [selectedAlternative, exercise]);

  function onSelectedAlternative(alternative: IExerciseAlternative) {
    setSelectedAlternative(alternative);
  }

  function handleShowResult() {
    if(exercise.exercise_type === 'discursive') {
      setShowResult(true);
      return;
    }

    if (!selectedAlternative) return;

    setShowResult(true);
    onShowCorrectAnswer(selectedAlternative);
  }

  function resetStates() {
    setSelectedAlternative(null);
    setShowResult(false);
    setShowCorrectAnswer(false);
  }

  const hasPreviousExercise = useCallback(() => {
    return currentExerciseIndex > 0;
  }, [currentExerciseIndex]);

  const hasNextExercise = useCallback(() => {
    return currentExerciseIndex < totalExercises - 1;
  }, [currentExerciseIndex, totalExercises]);

  const enableCheckAnswer = () => {
    if(exercise.exercise_type === 'discursive') {
      return false;
    }

    return !selectedAlternative || showResult;
  }

  const screenSize = useScreenSize();
  const windowBreakpoints = useWindowBreakpoints();

  useEffect(() => {
    if (exercise.answered) {
      setSelectedAlternative(
        exercise.alternatives.find((alternative) => alternative.selected) ||
          null
      );
      setShowCorrectAnswer(true);
      setShowResult(true);
    }
  }, [exercise]);

  function renderQuiz() {
    const isModal = screenSize <= windowBreakpoints.md;

    return (
      <Fragment>
        <div className='pointer-events-auto px-8'>
          <InnerHTMLContainer
            data={exercise.statement || ''}
            type='courseProgram'
            className={`img-center hyphens-auto !break-words ${isModal ? 'text-mono-color-darkest' : 'text-[#fff]'} mb-2`}
          />

          <AlternativesList
            spacing='small'
            alternatives={exercise.alternatives}
            selectedAlternativeId={selectedAlternative?.id}
            showCorrectAnswer={showCorrectAnswer}
            showResult={showResult}
            onSelectAlternative={onSelectedAlternative}
            type={'retry'}
          />

          {exercise.exercise_type === 'discursive' && showResult && (
            <div className='rounded-md border-hairline border-mono-color-white p-5'>
              <InnerHTMLContainer
                data={exercise.resolution}
                type='courseProgram'
                className='text-mono-color-white'
              />
            </div>
          )}

          {showResult && (
            <div className='my-3 flex flex-col justify-between gap-lg md:flex-row'>
              {exercise.exercise_type !== 'discursive' && (
                <Button
                  LeftIcon={'checklist'}
                  variant='text'
                  disabled={isCorrect() || showCorrectAnswer}
                  onClick={() => {
                    setShowCorrectAnswer(true);
                  }}
                  className='justify-center p-0 text-[#fff] md:justify-start'
                  id='button-de-modal-show-correct-answer'
                >
                  Ver gabarito
                </Button>
              )}

              <ContentActions.Root
                contentType={'Exercise'}
                contentId={exercise._id}
                contentSlug={exercise.slug}
                className={'ml-auto text-[#fff]'}
              >
                <ContentActions.Report className='text-[#fff]' />
              </ContentActions.Root>
            </div>
          )}
        </div>

        <div className='pointer-events-auto mt-2 flex items-center justify-between gap-sm bg-mono-color-white px-8 py-2 md:gap-xl'>
          <Button
            variant='outlined'
            onClick={() => {
              resetStates();
              handlePreviousExercise();
            }}
            disabled={!hasPreviousExercise()}
          >
            {screenSize >= windowBreakpoints.md
              ? 'Exercício anterior'
              : 'Anterior'}
          </Button>
          {showResult ? (
            <Button
              disabled={isCorrect() || showCorrectAnswer}
              onClick={() => {
                resetStates();
              }}
              className='flex-1 justify-center'
              id='button-de-modal-show-correct-answer'
            >
              Tentar novamente
            </Button>
          ) : (
            <Button
              className='flex-1 justify-center'
              onClick={handleShowResult}
              disabled={enableCheckAnswer()}
            >
              Conferir
            </Button>
          )}

          <Button
            variant='outlined'
            onClick={() => {
              resetStates();
              handleNextExercise();
            }}
            disabled={!hasNextExercise()}
          >
            {screenSize >= windowBreakpoints.md
              ? 'Próximo exercício'
              : 'Próximo'}
          </Button>
        </div>
      </Fragment>
    );
  }

  useEffect(() => {
    if (typeof window?.MathJax !== 'undefined') {
      window.MathJax.typesetPromise();
    }
  });

  if (screenSize <= windowBreakpoints.md) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title='Quiz da aula'>
        {renderQuiz()}
      </Modal>
    );
  }

  return renderQuiz();
}
